import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
    Radio,
    Lock,
    Eye,
    EyeSlash,
    ArrowLeft,
    CheckCircle,
    WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const resetPasswordTexts = {
    de: {
        title: "Neues Passwort setzen",
        subtitle: "Wähle ein starkes neues Passwort für dein WORLDofRADIO-Konto.",
        newPassword: "Neues Passwort",
        confirmPassword: "Passwort bestätigen",
        tokenMissing: "Reset-Token fehlt. Bitte fordere einen neuen Reset-Link an.",
        invalidPassword: "Bitte gib ein gültiges Passwort ein und bestätige es korrekt.",
        resetFailed: "Passwort konnte nicht zurückgesetzt werden.",
        resetSuccess: "Dein Passwort wurde erfolgreich zurückgesetzt.",
        savingPassword: "Passwort wird gespeichert...",
        setNewPassword: "Neues Passwort setzen",
        backToLogin: "Zurück zum Login",
        backToHome: "Zurück zur Startseite",
        hidePassword: "Passwort ausblenden",
        showPassword: "Passwort anzeigen",
        ruleLength: "Mindestens 8 Zeichen",
        ruleUppercase: "Ein Großbuchstabe",
        ruleNumber: "Eine Zahl",
        ruleSpecial: "Ein Sonderzeichen",
        ruleMatch: "Passwörter stimmen überein",
    },

    en: {
        title: "Set new password",
        subtitle: "Choose a strong new password for your WORLDofRADIO account.",
        newPassword: "New password",
        confirmPassword: "Confirm password",
        tokenMissing: "Reset token is missing. Please request a new reset link.",
        invalidPassword: "Please enter a valid password and confirm it correctly.",
        resetFailed: "Password reset failed.",
        resetSuccess: "Your password has been reset successfully.",
        savingPassword: "Saving password...",
        setNewPassword: "Set new password",
        backToLogin: "Back to login",
        backToHome: "Back to home",
        hidePassword: "Hide password",
        showPassword: "Show password",
        ruleLength: "At least 8 characters",
        ruleUppercase: "One uppercase letter",
        ruleNumber: "One number",
        ruleSpecial: "One special character",
        ruleMatch: "Passwords match",
    },

    no: {
        title: "Sett nytt passord",
        subtitle: "Velg et sterkt nytt passord for WORLDofRADIO-kontoen din.",
        newPassword: "Nytt passord",
        confirmPassword: "Bekreft passord",
        tokenMissing: "Tilbakestillingstoken mangler. Be om en ny tilbakestillingslenke.",
        invalidPassword: "Skriv inn et gyldig passord og bekreft det riktig.",
        resetFailed: "Tilbakestilling av passord mislyktes.",
        resetSuccess: "Passordet ditt er tilbakestilt.",
        savingPassword: "Lagrer passord...",
        setNewPassword: "Sett nytt passord",
        backToLogin: "Tilbake til innlogging",
        backToHome: "Tilbake til startsiden",
        hidePassword: "Skjul passord",
        showPassword: "Vis passord",
        ruleLength: "Minst 8 tegn",
        ruleUppercase: "Én stor bokstav",
        ruleNumber: "Ett tall",
        ruleSpecial: "Ett spesialtegn",
        ruleMatch: "Passordene er like",
    },

    ko: {
        title: "새 비밀번호 설정",
        subtitle: "WORLDofRADIO 계정에 사용할 강력한 새 비밀번호를 선택하세요.",
        newPassword: "새 비밀번호",
        confirmPassword: "비밀번호 확인",
        tokenMissing: "재설정 토큰이 없습니다. 새 재설정 링크를 요청하세요.",
        invalidPassword: "유효한 비밀번호를 입력하고 올바르게 확인하세요.",
        resetFailed: "비밀번호 재설정에 실패했습니다.",
        resetSuccess: "비밀번호가 성공적으로 재설정되었습니다.",
        savingPassword: "비밀번호 저장 중...",
        setNewPassword: "새 비밀번호 설정",
        backToLogin: "로그인으로 돌아가기",
        backToHome: "홈으로 돌아가기",
        hidePassword: "비밀번호 숨기기",
        showPassword: "비밀번호 보기",
        ruleLength: "최소 8자",
        ruleUppercase: "대문자 1개",
        ruleNumber: "숫자 1개",
        ruleSpecial: "특수문자 1개",
        ruleMatch: "비밀번호가 일치함",
    },

    fi: {
        title: "Aseta uusi salasana",
        subtitle: "Valitse vahva uusi salasana WORLDofRADIO-tilillesi.",
        newPassword: "Uusi salasana",
        confirmPassword: "Vahvista salasana",
        tokenMissing: "Palautustunnus puuttuu. Pyydä uusi palautuslinkki.",
        invalidPassword: "Syötä kelvollinen salasana ja vahvista se oikein.",
        resetFailed: "Salasanan palautus epäonnistui.",
        resetSuccess: "Salasanasi on palautettu onnistuneesti.",
        savingPassword: "Tallennetaan salasanaa...",
        setNewPassword: "Aseta uusi salasana",
        backToLogin: "Takaisin kirjautumiseen",
        backToHome: "Takaisin etusivulle",
        hidePassword: "Piilota salasana",
        showPassword: "Näytä salasana",
        ruleLength: "Vähintään 8 merkkiä",
        ruleUppercase: "Yksi iso kirjain",
        ruleNumber: "Yksi numero",
        ruleSpecial: "Yksi erikoismerkki",
        ruleMatch: "Salasanat täsmäävät",
    },
};

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language } = useLanguage();

    const text = resetPasswordTexts[language] || resetPasswordTexts.en;

    const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState("");

    const passwordChecks = {
        length: newPassword.length >= 8,
        uppercase: /[A-Z]/.test(newPassword),
        number: /\d/.test(newPassword),
        special: /[^A-Za-z0-9]/.test(newPassword),
        match: newPassword.length > 0 && newPassword === confirmPassword,
    };

    const isPasswordValid =
        passwordChecks.length &&
        passwordChecks.uppercase &&
        passwordChecks.number &&
        passwordChecks.special &&
        passwordChecks.match;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            setError(text.tokenMissing);
            return;
        }

        if (!isPasswordValid) {
            setError(text.invalidPassword);
            return;
        }

        setLoading(true);
        setError("");
        setSuccessMessage("");

        try {
            const response = await fetch("/api/auth/reset-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    token,
                    new_password: newPassword,
                }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.detail || text.resetFailed);
            }

            setSuccessMessage(text.resetSuccess);

            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 1800);
        } catch (err) {
            setError(err.message || text.resetFailed);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center gap-3 mb-8">
                    <Radio size={40} weight="duotone" className="text-cyan-400" />
                    <span
                        className="text-3xl font-black tracking-tighter text-white"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        WORLDofRADIO
                    </span>
                </div>

                <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
                    <h1
                        className="text-2xl sm:text-3xl tracking-tight font-bold mb-2 text-white"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        {text.title}
                    </h1>

                    <p className="text-sm text-gray-400 mb-8">
                        {text.subtitle}
                    </p>

                    {!token && (
                        <div
                            className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6 text-sm text-red-400 flex gap-2"
                            role="alert"
                        >
                            <WarningCircle size={20} weight="fill" className="flex-shrink-0" />
                            <span>{text.tokenMissing}</span>
                        </div>
                    )}

                    {error && (
                        <div
                            className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6 text-sm text-red-400 flex gap-2"
                            role="alert"
                        >
                            <WarningCircle size={20} weight="fill" className="flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {successMessage && (
                        <div
                            className="bg-green-500/10 border border-green-500/40 rounded-xl p-4 mb-6 text-sm text-green-400 flex gap-2"
                            role="status"
                        >
                            <CheckCircle size={20} weight="fill" className="flex-shrink-0" />
                            <span>{successMessage}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label htmlFor="newPassword" className="text-white mb-2 block">
                                {text.newPassword}
                            </Label>

                            <div className="relative">
                                <Lock
                                    size={20}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                                />

                                <Input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showNewPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        if (error) setError("");
                                    }}
                                    className="pl-12 pr-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                                    placeholder="••••••••"
                                    required
                                    disabled={loading || !token}
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword((current) => !current)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors disabled:opacity-50"
                                    aria-label={showNewPassword ? text.hidePassword : text.showPassword}
                                    disabled={loading || !token}
                                >
                                    {showNewPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="confirmPassword" className="text-white mb-2 block">
                                {text.confirmPassword}
                            </Label>

                            <div className="relative">
                                <Lock
                                    size={20}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                                />

                                <Input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        if (error) setError("");
                                    }}
                                    className="pl-12 pr-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                                    placeholder="••••••••"
                                    required
                                    disabled={loading || !token}
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword((current) => !current)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors disabled:opacity-50"
                                    aria-label={
                                        showConfirmPassword ? text.hidePassword : text.showPassword
                                    }
                                    disabled={loading || !token}
                                >
                                    {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-white/10 bg-black/30 p-4 text-xs">
                            <PasswordRule active={passwordChecks.length} label={text.ruleLength} />
                            <PasswordRule active={passwordChecks.uppercase} label={text.ruleUppercase} />
                            <PasswordRule active={passwordChecks.number} label={text.ruleNumber} />
                            <PasswordRule active={passwordChecks.special} label={text.ruleSpecial} />
                            <PasswordRule active={passwordChecks.match} label={text.ruleMatch} />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || !token || !isPasswordValid}
                            className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-black font-bold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? text.savingPassword : text.setNewPassword}
                        </Button>
                    </form>

                    <div className="text-center mt-6">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                        >
                            <ArrowLeft size={16} />
                            {text.backToLogin}
                        </Link>
                    </div>
                </div>

                <div className="text-center mt-6">
                    <Link
                        to="/"
                        className="text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                    >
                        ← {text.backToHome}
                    </Link>
                </div>
            </div>
        </div>
    );
};

const PasswordRule = ({ active, label }) => {
    return (
        <div className={active ? "text-cyan-300" : "text-gray-500"}>
            <span className="mr-2">{active ? "✓" : "•"}</span>
            {label}
        </div>
    );
};

export default ResetPassword;